import React, { FormEvent, useEffect, useState } from "react";
import { CalendarIcon, CheckCircle2Icon, ChevronDownIcon, CircleDollarSignIcon, FilePlus2Icon, XIcon } from "lucide-react";
import { borrower } from "../data/contact";
type NewLoanForm = {
  loanName: string;
  loanPurpose: string;
  status: string;
  propertyType: string;
  propertyUse: string;
  loanType: string;
  employmentW2: boolean;
  employmentSelfEmployed: boolean;
  employmentRetired: boolean;
  totalLoanAmount: string;
  firstTimeHomeBuyer: boolean;
  interestRate: string;
  purchasePrice: string;
  loanAmount: string;
  percentDown: string;
  appraisedValue: string;
  downPayment: string;
  totalPayment: string;
  loanProgram: string;
  yearBuilt: string;
  fileOpenDate: string;
  docType: string;
  smsUsed: string;
};
const initialForm: NewLoanForm = {
  loanName: `${borrower.firstName} ${borrower.lastName} Loan`,
  loanPurpose: "",
  status: "Application Taken",
  propertyType: "",
  propertyUse: "",
  loanType: "",
  employmentW2: false,
  employmentSelfEmployed: false,
  employmentRetired: false,
  totalLoanAmount: "",
  firstTimeHomeBuyer: false,
  interestRate: "",
  purchasePrice: "",
  loanAmount: "",
  percentDown: "",
  appraisedValue: "",
  downPayment: "",
  totalPayment: "",
  loanProgram: "",
  yearBuilt: "",
  fileOpenDate: "2026-07-22",
  docType: "",
  smsUsed: ""
};
const selectClass = "w-full appearance-none rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition hover:border-slate-300 focus:border-[#1a73e8] focus:ring-2 focus:ring-blue-100";
const inputClass = "w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none transition placeholder:text-slate-300 hover:border-slate-300 focus:border-[#1a73e8] focus:ring-2 focus:ring-blue-100";
function FieldLabel({
  label,
  required = false
}: {
  label: string;
  required?: boolean;
}) {
  return <label className="mb-1.5 block text-xs font-medium text-slate-600" data-id="element-23" data-mp="r0ic6lkdt3n9">
      {required && <span className="mr-0.5 text-rose-500" data-id="element-24" data-mp="2q50czc0t0hev">*</span>}
      {label}
    </label>;
}
function SelectField({
  label,
  value,
  options,
  onChange,
  required = false
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  required?: boolean;
}) {
  return <div data-id="element-25" data-mp="34u4vkubignni">
      <FieldLabel label={label} required={required} data-id="element-26" data-mp="34twgcvrx1695" />
      <div className="relative" data-id="element-27" data-mp="277pdprf69nzf">
        <select value={value} onChange={event => onChange(event.target.value)} className={selectClass} data-id="element-28" data-mp="278gzybmvq9d1">
          <option value="" data-id="element-29" data-mp="2c692dzl5hdvh">Select {label.toLowerCase()}</option>
          {options.map(option => <option key={option} value={option} data-id="element-30" data-mp="2h3inx45f55e1">{option}</option>)}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-3 top-3 h-4 w-4 text-slate-400" data-id="element-31" data-mp="2m57y6hkthf12" />
      </div>
    </div>;
}
function TextField({
  label,
  value,
  onChange,
  type = "text",
  prefix,
  suffix,
  readOnly = false
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "number" | "date";
  prefix?: string;
  suffix?: string;
  readOnly?: boolean;
}) {
  return <div data-id="element-32" data-mp="1gxowj12bu6y3">
      <FieldLabel label={label} data-id="element-33" data-mp="1gyzz4xk1d8nm" />
      <div className="relative" data-id="element-34" data-mp="1c5nd8v6qrfyk">
        {prefix && <span className="pointer-events-none absolute left-3 top-2.5 text-sm text-slate-400" data-id="element-35" data-mp="1c4lz2hufidcm">{prefix}</span>}
        <input type={type} value={value} readOnly={readOnly} onChange={event => onChange(event.target.value)} className={`${inputClass} ${prefix ? "pl-7" : ""} ${suffix ? "pr-9" : ""} ${readOnly ? "bg-slate-50 text-slate-500" : ""}`} data-id="element-36" data-mp="176u239exg11k" />
        {suffix && <span className="pointer-events-none absolute right-3 top-2.5 text-sm text-slate-400" data-id="element-37" data-mp="t9f7lq9th32q">{suffix}</span>}
      </div>
    </div>;
}
function FormSection({
  title,
  icon,
  children
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return <section className="rounded-xl border border-slate-200 bg-white" data-id="element-38" data-mp="2inka8y2wv2xt">
      <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3" data-id="element-39" data-mp="1g8b8slgkikc0">
        <span className="text-[#1a73e8]" data-id="element-40" data-mp="1ka6a3pyi0xps">{icon}</span>
        <h3 className="text-sm font-semibold text-slate-800" data-id="element-41" data-mp="1k9wmua4u94ns">{title}</h3>
      </div>
      <div className="p-5" data-id="element-42" data-mp="169vl6sgr1t79">{children}</div>
    </section>;
}
export function CreateLoanModal({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [form, setForm] = useState<NewLoanForm>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const borrowerName = `${borrower.firstName} ${borrower.lastName}`;
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const update = <K extends keyof NewLoanForm,>(key: K, value: NewLoanForm[K]) => {
    setForm(current => ({
      ...current,
      [key]: value
    }));
  };
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form.loanPurpose || !form.propertyType || !form.loanType || !form.loanProgram) {
      setError("Complete the required loan details before creating this loan.");
      return;
    }
    setError("");
    setSubmitted(true);
  };
  const handleClose = () => {
    setForm(initialForm);
    setSubmitted(false);
    setError("");
    onClose();
  };
  return <div className="fixed inset-0 z-50 flex items-end bg-slate-950/30 p-0 sm:items-center sm:justify-center sm:p-6" role="presentation" onMouseDown={event => {
    if (event.target === event.currentTarget) handleClose();
  }} data-id="element-43" data-mp="dugxqgnes114">
      <div className="flex max-h-[96vh] w-full max-w-5xl flex-col overflow-hidden rounded-t-2xl bg-slate-50 shadow-2xl sm:max-h-[90vh] sm:rounded-2xl" role="dialog" aria-modal="true" aria-labelledby="new-loan-title" data-id="element-44" data-mp="11m5npqcrcdew">
        <div className="flex items-start justify-between border-b border-slate-200 bg-white px-5 py-4 sm:px-6" data-id="element-45" data-mp="13qrbqwbcj46q">
          <div className="flex gap-3" data-id="element-46" data-mp="18qdsvguap4kt">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#1a73e8]" data-id="element-47" data-mp="18n9ghw6of2j2"><FilePlus2Icon className="h-5 w-5" data-id="element-48" data-mp="1dmm1schwg1jj" /></span>
            <div data-id="element-49" data-mp="1ig70k6rbgzjs">
              <h2 id="new-loan-title" className="text-base font-semibold text-slate-900" data-id="element-50" data-mp="1igq8qvr609km">Create New Loan</h2>
              <p className="mt-0.5 text-xs text-slate-500" data-id="element-51" data-mp="1ndyvzar673sq">Start a new loan record for {borrowerName}.</p>
            </div>
          </div>
          <button type="button" onClick={handleClose} aria-label="Close create new loan form" className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-200" data-id="element-52" data-mp="1wf6hstci2c5x"><XIcon className="h-5 w-5" data-id="element-53" data-mp="21xddfyt9guj" /></button>
        </div>

        {submitted ? <div className="flex min-h-[420px] flex-1 flex-col items-center justify-center px-6 text-center" data-id="element-54" data-mp="6z718r6jxkg9">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600" data-id="element-55" data-mp="3vmwxkik7j1v8"><CheckCircle2Icon className="h-7 w-7" data-id="element-56" data-mp="3qu3qvp56hjgi" /></span>
            <h3 className="mt-4 text-lg font-semibold text-slate-900" data-id="element-57" data-mp="3lw22coozgrxn">Loan ready to create</h3>
            <p className="mt-2 max-w-sm text-sm leading-6 text-slate-500" data-id="element-58" data-mp="3gwz6nuuw7y2c">{form.loanName || `${borrowerName} Loan`} has been prepared for {borrowerName}. In Salesforce, this action will create the related Loan__c record.</p>
            <button type="button" onClick={handleClose} className="mt-6 rounded-lg bg-[#1a73e8] px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200" data-id="element-59" data-mp="dfp8z7juzi9f">Done</button>
          </div> : <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col" data-id="element-60" data-mp="3k5g6u0lczow">
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6" data-id="element-61" data-mp="2t8xywo5jglnz">
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5 text-xs text-blue-800" data-id="element-62" data-mp="2o78s5kpcmu5p"><span className="h-1.5 w-1.5 rounded-full bg-[#1a73e8]" data-id="element-63" data-mp="3dv4e6kn8hl91" /> Borrower is linked automatically from this Contact.</div>
              {error && <div role="alert" className="mb-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-700" data-id="element-64" data-mp="34w0txdcbyzsy">{error}</div>}
              <div className="space-y-4" data-id="element-65" data-mp="38y49ad24eoji">
                <FormSection title="Loan Information" icon={<FilePlus2Icon className="h-4 w-4" data-id="element-67" data-mp="ikhlih3le6k" />} data-id="element-66" data-mp="hj477cm0flo">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-68" data-mp="5gma0u0eypup">
                    <TextField label="Loan Name" value={form.loanName} onChange={value => update("loanName", value)} data-id="element-69" data-mp="5iqd59qhnktx" />
                    <TextField label="Borrower Name" value={borrowerName} onChange={() => undefined} readOnly data-id="element-70" data-mp="3rw7272xsh5ml" />
                    <SelectField label="Loan Purpose" required value={form.loanPurpose} onChange={value => update("loanPurpose", value)} options={["Purchase", "Refinance", "Cash Out Refinance", "Home Equity"]} data-id="element-71" data-mp="jgna478utt94" />
                    <SelectField label="Status" required value={form.status} onChange={value => update("status", value)} options={["Application Taken", "Application Started", "Processing", "Pre-Approval"]} data-id="element-72" data-mp="28wqz2rrqqbak" />
                    <SelectField label="Property Type" required value={form.propertyType} onChange={value => update("propertyType", value)} options={["Single Family", "Condominium", "Townhome", "2–4 Unit", "Manufactured Home"]} data-id="element-73" data-mp="2imaibyjd875y" />
                    <SelectField label="Property Use" value={form.propertyUse} onChange={value => update("propertyUse", value)} options={["Primary Residence", "Second Home", "Investment Property"]} data-id="element-74" data-mp="1pywseke34bvc" />
                    <SelectField label="Loan Type" required value={form.loanType} onChange={value => update("loanType", value)} options={["Conventional", "FHA", "VA", "USDA", "Jumbo"]} data-id="element-75" data-mp="1zy5llb14lmgn" />
                  </div>
                </FormSection>

                <FormSection title="Borrower & Employment" icon={<CircleDollarSignIcon className="h-4 w-4" data-id="element-77" data-mp="36cepgfvrip1p" />} data-id="element-76" data-mp="36dfxscle8xfq">
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-[1fr_1.2fr] md:items-start" data-id="element-78" data-mp="ib8tnpvj3nsy">
                    <div data-id="element-79" data-mp="dbmg1fbsg5jm"><FieldLabel label="Employment Type" data-id="element-80" data-mp="dcxi9apl558m" /><div className="space-y-2" data-id="element-81" data-mp="dco029as82dh">
                      {([["employmentW2", "W2"], ["employmentSelfEmployed", "Self-Employed"], ["employmentRetired", "Retired"]] as const).map(([key, label]) => <label key={key} className="flex cursor-pointer items-center gap-2.5 text-sm text-slate-700" data-id="element-82" data-mp="3ljlhap0qjj6"><input type="checkbox" checked={form[key]} onChange={event => update(key, event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-[#1a73e8] focus:ring-blue-200" data-id="element-83" data-mp="3utxis8lry9no" />{label}</label>)}
                    </div></div>
                    <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 px-3 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-84" data-mp="3gsut7lvkmh80"><input type="checkbox" checked={form.firstTimeHomeBuyer} onChange={event => update("firstTimeHomeBuyer", event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-[#1a73e8] focus:ring-blue-200" data-id="element-85" data-mp="1jzt3ln2o3c1k" />First Time Home Buyer</label>
                  </div>
                </FormSection>

                <FormSection title="Loan & Property Numbers" icon={<CircleDollarSignIcon className="h-4 w-4" data-id="element-87" data-mp="1waalma4ois2q" />} data-id="element-86" data-mp="1rhr5p6iirkur">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-88" data-mp="219nilnp9lfj1">
                    <TextField label="Total Loan Amount" value={form.totalLoanAmount} onChange={value => update("totalLoanAmount", value)} type="number" prefix="$" data-id="element-89" data-mp="21bht082wtp8v" />
                    <TextField label="Interest Rate" value={form.interestRate} onChange={value => update("interestRate", value)} type="number" suffix="%" data-id="element-90" data-mp="18lhx2yqdzmi5" />
                    <TextField label="Purchase Price" value={form.purchasePrice} onChange={value => update("purchasePrice", value)} type="number" prefix="$" data-id="element-91" data-mp="1iivybr3qo4pv" />
                    <TextField label="Loan Amount" value={form.loanAmount} onChange={value => update("loanAmount", value)} type="number" prefix="$" data-id="element-92" data-mp="2p1c4ombht7rj" />
                    <TextField label="Percent Down" value={form.percentDown} onChange={value => update("percentDown", value)} type="number" suffix="%" data-id="element-93" data-mp="tva08qd1keq0" />
                    <TextField label="Appraised Value" value={form.appraisedValue} onChange={value => update("appraisedValue", value)} type="number" prefix="$" data-id="element-94" data-mp="ozcl2ndileet" />
                    <TextField label="Down Payment" value={form.downPayment} onChange={value => update("downPayment", value)} type="number" prefix="$" data-id="element-95" data-mp="ywh80pf6pdwo" />
                    <TextField label="Total Payment" value={form.totalPayment} onChange={value => update("totalPayment", value)} type="number" prefix="$" data-id="element-96" data-mp="66qt4e5qa7zi" />
                    <TextField label="Year Built" value={form.yearBuilt} onChange={value => update("yearBuilt", value)} type="number" data-id="element-97" data-mp="jz6oqtwav3qe" />
                  </div>
                </FormSection>

                <FormSection title="Program & Dates" icon={<CalendarIcon className="h-4 w-4" data-id="element-99" data-mp="3jldbtp9wbo1f" />} data-id="element-98" data-mp="3nlffu533stjv">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-id="element-100" data-mp="3jkvjh3qcttys">
                    <SelectField label="Loan Program (1st TD)" required value={form.loanProgram} onChange={value => update("loanProgram", value)} options={["30-Year Fixed", "20-Year Fixed", "15-Year Fixed", "5/6 ARM", "FHA 30-Year Fixed", "VA 30-Year Fixed"]} data-id="element-101" data-mp="39h6sz3fvpect" />
                    <TextField label="File Open Date" value={form.fileOpenDate} onChange={value => update("fileOpenDate", value)} type="date" data-id="element-102" data-mp="34idb6ytk716u" />
                    <SelectField label="Doc Type (1st TD)" value={form.docType} onChange={value => update("docType", value)} options={["Full Documentation", "Bank Statement", "Asset Depletion", "No Ratio"]} data-id="element-103" data-mp="3s99asi49b0on" />
                    <SelectField label="SMS Used?" value={form.smsUsed} onChange={value => update("smsUsed", value)} options={["Yes", "No"]} data-id="element-104" data-mp="3i98msivfzwe4" />
                  </div>
                </FormSection>
              </div>
            </div>
            <div className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6" data-id="element-105" data-mp="26wy2z1yofalt"><p className="text-xs text-slate-400" data-id="element-106" data-mp="2s1w2nv8b95cq"><span className="text-rose-500" data-id="element-107" data-mp="2s25lmyuxuc8x">*</span> Required fields</p><div className="flex gap-2" data-id="element-108" data-mp="2n9ulbuwacofu"><button type="button" onClick={handleClose} className="rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-200" data-id="element-109" data-mp="2n8jhxw6r5ip9">Cancel</button><button type="submit" className="rounded-lg bg-[#1a73e8] px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200" data-id="element-110" data-mp="2da3m5fws5l9f">Create Loan</button></div></div>
          </form>}
      </div>
    </div>;
}